import { useState } from 'react';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function getVisiblePages(current: number, totalPages: number): Array<number | 'ellipsis'> {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: Array<number | 'ellipsis'> = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);

  if (start > 2) pages.push('ellipsis');
  for (let pageNumber = start; pageNumber <= end; pageNumber += 1) {
    pages.push(pageNumber);
  }
  if (end < totalPages - 1) pages.push('ellipsis');
  pages.push(totalPages);
  return pages;
}

type TableFooterProps = {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize: number) => void;
  onPageSizeChange: (pageSize: number, page: number) => void;
};

export function TableFooter({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: TableFooterProps) {
  const [goToPage, setGoToPage] = useState<number | ''>('');
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = total === 0 ? 0 : Math.min(page * pageSize, total);
  const pagerButton =
    'flex h-8 min-w-8 items-center justify-center rounded border border-gray-300 bg-white px-2 text-sm text-gray-600 hover:border-[#F17A20] hover:text-[#F17A20] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-gray-300 disabled:hover:text-gray-600';
  const activePageButton =
    'flex h-8 min-w-8 items-center justify-center rounded border border-[#d96a12] bg-[#F17A20] px-2 text-sm text-white';

  const handleGoToPage = () => {
    if (goToPage && goToPage >= 1 && goToPage <= totalPages) {
      onPageChange(goToPage, pageSize);
      setGoToPage('');
    }
  };

  return (
    <div className="flex h-12 shrink-0 items-center justify-between border-t border-gray-200 px-4 text-sm text-gray-600">
      <span>Tổng số: {total}</span>
      <div className="flex items-center gap-3">
        <span>Số dòng/trang</span>
        <select
          value={pageSize}
          onChange={(event) => onPageSizeChange(Number(event.target.value), 1)}
          className="h-8 rounded border border-gray-300 bg-white px-2 text-sm text-gray-700"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        <span className="min-w-[52px] text-center">
          {start} - {end}
        </span>
        <div className="flex items-center gap-1">
          <button type="button" className={pagerButton} disabled={page <= 1} onClick={() => onPageChange(1, pageSize)} aria-label="Trang đầu">
            «
          </button>
          <button type="button" className={pagerButton} disabled={page <= 1} onClick={() => onPageChange(page - 1, pageSize)} aria-label="Trang trước">
            ‹
          </button>
          {getVisiblePages(page, totalPages).map((item, index) =>
            item === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="px-1 text-gray-400">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={item === page ? activePageButton : pagerButton}
                onClick={() => onPageChange(item, pageSize)}
                aria-label={`Trang ${item}`}
                aria-current={item === page ? 'page' : undefined}
              >
                {item}
              </button>
            ),
          )}
          <button type="button" className={pagerButton} disabled={page >= totalPages} onClick={() => onPageChange(page + 1, pageSize)} aria-label="Trang sau">
            ›
          </button>
          <button type="button" className={pagerButton} disabled={page >= totalPages} onClick={() => onPageChange(totalPages, pageSize)} aria-label="Trang cuối">
            »
          </button>
        </div>
        <div className="flex items-center gap-1">
          <span>Đi đến</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={goToPage}
            onChange={(event) => setGoToPage(event.target.value ? Number(event.target.value) : '')}
            onKeyDown={(event) => event.key === 'Enter' && handleGoToPage()}
            className="h-8 w-14 rounded border border-gray-300 bg-white px-2 text-sm text-gray-700"
            aria-label="Đi đến trang"
          />
          <span>trang</span>
        </div>
      </div>
    </div>
  );
}
